import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnDestroy,
} from '@angular/core';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { DateTime } from 'luxon';
import { TranslateService } from '@ngx-translate/core';
import {
  CategoryService,
  profitAndLossCol,
  profitAndLossRow,
} from '../../../categories/category.service';
import { MyDpDateService } from '../../services/my-dp-date.service';
import { OrganizationService } from 'src/app/organizations/organization.service';
import {
  catchError,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import * as moment from 'moment';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Organization } from '../../sdk';
import { FormBuilder, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-profit-and-loss',
  templateUrl: './profit-and-loss.component.html',
  styleUrls: ['./profit-and-loss.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfitAndLossComponent implements OnInit, OnDestroy {
  @Input() from: NgbDateStruct;
  @Input() load: Observable<void>;
  @Input() options = false;
  @Input() serviceName: string;
  @Input() to: NgbDateStruct;
  @Output() loading = new EventEmitter<boolean>();

  currencyId: string;
  expandableRows: number[] = [];
  expanded: number[] = [];
  expandedRowIdx: number;
  form: FormGroup;
  locale: string;
  report: Observable<any[]>;
  subRowsPerRow: { idx: number; subRowIdxs: number[] }[] = [];
  private _loading: boolean;
  private _name: string;
  private onDestroy = new Subject<void>();

  @Input()
  set name(name: string) {
    this._name = name;
  }
  get name(): string {
    return `profit-and-loss.header.${this._name}`;
  }

  constructor(
    private categoryService: CategoryService,
    private formBuilder: FormBuilder,
    private myDpDateService: MyDpDateService,
    private organizationService: OrganizationService,
    private translateService: TranslateService,
  ) {}

  formatYearMonth(yearMonth: string): string {
    return DateTime.fromISO(yearMonth)
      .setLocale(this.locale)
      .toLocaleString({ month: 'short', year: 'numeric' });
  }

  getValue(col: profitAndLossCol): Observable<string> {
    const v = this.categoryService.getValue(col);

    // No value or not translatable string with dots
    if (!v || v.indexOf('.') === -1) return of(v);

    return this.translateService.get(v);
  }

  isNumber(value: string | number): boolean {
    return typeof value === 'number';
  }

  isBold(
    listLength: number,
    rowIdx: number,
    colIdx: number,
    col: { key: string; value: number | string },
  ): boolean {
    return (
      rowIdx === listLength - 1 ||
      (colIdx === 0 &&
        (col.key === 'profit' ||
          col.key.indexOf('header_') !== -1 ||
          col.key.indexOf('_total') !== -1 ||
          col.key.indexOf('total_') !== -1))
    );
  }

  isMuted(
    listLength: number,
    rowIdx: number,
    colIdx: number,
    col: { key: string; value: number | string },
  ): boolean {
    return colIdx === 0 && col.key.indexOf('header_') !== -1;
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      categorized: [false],
    });

    this.report = this.organizationService.active.pipe(
      filter((organization) => !!organization),
      switchMap((organization: Organization) =>
        combineLatest([
          of(organization),
          this.form.controls.categorized.valueChanges.pipe(startWith(false)),
          this.load.pipe(
            startWith([]),
            // map(() => organization),
          ),
        ]),
      ),
      tap(() => this.setLoading(true)),
      map(([organization, categorized]) => {
        this.locale = organization.locale;
        this.currencyId = this.organizationService.findDefault(
          organization,
          'currencyId',
        ).value;

        const fromString = moment
          .utc(this.myDpDateService.parse(this.from))
          .format('Y-MM');

        const toString = moment
          .utc(this.myDpDateService.parse(this.to))
          .format('Y-MM');

        return {
          organization,
          controls: {
            from: fromString,
            to: toString,
          },
          categorized,
        };
      }),
      switchMap(({ organization, controls, categorized }) =>
        this.organizationService[this.serviceName](
          organization.id,
          controls.from,
          controls.to,
          { categorized: categorized, deferred: categorized },
        ),
      ),
      tap((data) => this.setLoading(false)),
      catchError((e) => {
        // TODO: Toaster
        this.setLoading(false);
        throw e;
      }),
      shareReplay(1),
    ) as Observable<any[]>;

    this.report.pipe().subscribe((report) => {
      this.expandableRows = [];
      this.expandedRowIdx = -1;
      this.subRowsPerRow = [];
      this.expanded = [];

      report.forEach((row, idx) => this.pushToRows(row, idx));
    });
  }

  onDownload(): void {
    const from = DateTime.fromISO(
      this.myDpDateService.parse(this.from),
    ).toFormat('y_LL');
    const to = DateTime.fromISO(this.myDpDateService.parse(this.to)).toFormat(
      'y_LL',
    );
    const categorized = this.form.controls.categorized ? '-categorized' : '';
    this.categoryService.downloadPnLCSV(
      this.report,
      `${this.name}-${from}-${to}${categorized}`,
    );
  }

  onToggleExpanded(rowIdx: number): void {
    if (!this.expandableRows.includes(rowIdx)) {
      return;
    }

    const row = this.subRowsPerRow.find((row) => row.idx === rowIdx);

    if (row) {
      if (this.expandedRowIdx !== rowIdx) {
        this.expanded = [...row.subRowIdxs];
        this.expandedRowIdx = rowIdx;
      } else {
        this.expanded = [];
        this.expandedRowIdx = -1;
      }
    }
  }

  documentLink(col: profitAndLossCol): any[] {
    const { documentId } = col;

    if (!documentId) {
      return;
    }

    const direction = !col.outgoing ? 'o' : 'i';

    return ['../../documents', direction, 'view', documentId];
  }

  pushToRows(row: profitAndLossRow, rowIdx: number): void {
    const isDocumentRow = row[0].drilldown;
    let prevRow: any;

    if (isDocumentRow) {
      const lastIdx = this.subRowsPerRow.length - 1;
      prevRow = this.subRowsPerRow[lastIdx];

      if (prevRow && prevRow.idx >= rowIdx) {
        prevRow = null;
      }
    }

    // Previous is main row
    if (prevRow) {
      prevRow.subRowIdxs.push(rowIdx);

      // Add to expandable only if it has sub rows
      this.expandableRows.push(prevRow.idx);
    } else {
      // Previous is main row
      this.subRowsPerRow.push({ idx: rowIdx, subRowIdxs: [] });
    }
  }

  private setLoading(loading: boolean) {
    this._loading = loading;
    this.loading.emit(this._loading);
  }
}
